import React, { useState, useRef, useContext, useEffect } from 'react';
import YouTube from 'react-youtube';
import cx from 'classnames';

import { useTranslation } from 'react-i18next';
import useStyles from 'hooks/useStyles';
import { useSelector, useDispatch } from 'react-redux';

import ApplicationContext from 'components/ApplicationContext';
import SnapaskHeader from 'components/SnapaskHeader';
import SnapaskFooter from 'components/SnapaskFooter';
import Link from 'components/Link';
import { BackIcon } from 'components/Icon';
import Tabs from 'components/Tabs';
import Button from 'components/Button';
import { goBack } from 'reduxModules/navigation/action';
import { sendGTMEvent } from 'reduxModules/googleTagManager/action';
import { getInstructorProfile } from 'reduxModules/instructorProfile/selector';
import useGetOfficialSiteURL from 'hooks/useGetOfficialSiteURL';
import {
  COURSE_TYPE,
  REGULAR_CLASS_TYPE,
  INSTRUCTOR_CONTENT_LIST_TEMPLATE,
} from 'constants/routingPath';
import {
  INSTRUCTOR_ALL_RC_VIEW,
  INSTRUCTOR_ALL_COURSE_VIEW,
} from 'constants/gtmEvent';

import { number } from 'prop-types';
import InstructorAbout from './components/InstructorAbout';
import InstructorCard from './components/InstructorCard';
import InstructorContentList from './components/InstructorContentList';

import styles from './InstructorProfile.scss';

function InstructorProfile({ id }) {
  useStyles(styles);
  const { t } = useTranslation(['instructorProfile']);
  const dispatch = useDispatch();

  const {
    screenWidth: { isTablet },
  } = useContext(ApplicationContext);

  const contentListRef = useRef(null);
  const playerRef = useRef(null);

  const { instructor, about, liveTopics, courses } = useSelector(
    getInstructorProfile,
  );
  const {
    name,
    avatarUrl,
    subjectString,
    liveTopicCount,
    courseCount,
    hashtagString,
    askableTutorId,
    active,
    videoId,
    videoSource,
  } = instructor;

  const getOfficialSiteURL = useGetOfficialSiteURL();
  const getInstructorContentUrl = (routingPath, type) =>
    getOfficialSiteURL(routingPath, { id, type });

  const showAboutSection = about.length > 0;
  const showTopicList = liveTopics.length > 0;
  const showCourseList = courses.length > 0;
  const showContentSection = showTopicList || showCourseList;

  const [contentMobileTab, setContentMobileTab] = useState(REGULAR_CLASS_TYPE);

  const eventNameMapping = {
    [REGULAR_CLASS_TYPE]: INSTRUCTOR_ALL_RC_VIEW,
    [COURSE_TYPE]: INSTRUCTOR_ALL_COURSE_VIEW,
  };

  const handleMoreTextLinkClick = contentType => {
    dispatch(
      sendGTMEvent({
        eventName: eventNameMapping[contentType],
        data: {
          instructorId: id,
          instructorName: name,
          instructorHashtag: hashtagString,
          subjects: subjectString,
          tutorService: askableTutorId !== null,
        },
      }),
    );
  };

  const getShowMoreLink = contentList => {
    if (isTablet) {
      return contentList.length > 2;
    }
    return contentList.length > 3;
  };

  const instructorCourseLink = getInstructorContentUrl(
    INSTRUCTOR_CONTENT_LIST_TEMPLATE,
    COURSE_TYPE,
  );

  const instructorLiveLink = getInstructorContentUrl(
    INSTRUCTOR_CONTENT_LIST_TEMPLATE,
    REGULAR_CLASS_TYPE,
  );

  const instructorTopicList = (
    <InstructorContentList
      className={styles.contentList}
      title={t('instructorProfile.regularClassListTitle')}
      moreTextLink={instructorLiveLink}
      onMoreTextLinkClick={() => handleMoreTextLinkClick(REGULAR_CLASS_TYPE)}
      contentList={liveTopics}
      showMoreLink={getShowMoreLink(liveTopics)}
      instructor={{ id, name }}
    />
  );

  const instructorCourseList = (
    <InstructorContentList
      className={styles.contentList}
      title={t('instructorProfile.courseListTitle')}
      moreTextLink={instructorCourseLink}
      onMoreTextLinkClick={() => handleMoreTextLinkClick(COURSE_TYPE)}
      contentList={courses}
      showMoreLink={getShowMoreLink(courses)}
    />
  );

  const tabComponentMapping = {
    [REGULAR_CLASS_TYPE]: {
      show: showTopicList,
      title: t('instructorProfile.regularClassListTitle'),
      component: instructorTopicList,
      ctaLink: instructorLiveLink,
      showMoreLink: getShowMoreLink(liveTopics),
    },
    [COURSE_TYPE]: {
      show: showCourseList,
      title: t('instructorProfile.courseListTitle'),
      component: instructorCourseList,
      ctaLink: instructorCourseLink,
      showMoreLink: getShowMoreLink(courses),
    },
  };

  const contentMobileTabPane = [REGULAR_CLASS_TYPE, COURSE_TYPE]
    .filter(type => tabComponentMapping[type].show)
    .map(key => {
      const { title, component } = tabComponentMapping[key];

      return (
        <Tabs.TabPane key={key} tabKey={key} tabTitle={title}>
          {component}
        </Tabs.TabPane>
      );
    });

  const onContentMobileChange = tabKey => setContentMobileTab(tabKey);
  const getContentMobileCtaButton = currentContent => {
    if (!tabComponentMapping[currentContent].showMoreLink) return null;
    return (
      <Link to={tabComponentMapping[currentContent].ctaLink}>
        <Button text={t('seeMoreCta')} block className={styles.seeMoreButton} />
      </Link>
    );
  };

  const onInstructorCardCtaClick = () => {
    contentListRef.current.scrollIntoView();
  };

  const youtubeOptions = {
    height: '100%',
    width: '100%',
    playerVars: {
      // https://developers.google.com/youtube/player_parameters
      autoplay: 1,
      modestbranding: 1,
    },
  };

  useEffect(() => {
    if (!playerRef.current) {
      return;
    }
    playerRef.current.internalPlayer.mute();
  }, [playerRef]);

  return (
    <>
      <SnapaskHeader />
      <div className={styles.root}>
        <div
          className={cx(styles.header, {
            [styles.videoHeader]: !!videoId,
            [styles.avatarHeader]: !videoId,
          })}
        >
          <div
            onClick={() => dispatch(goBack())}
            className={styles.backIconWrapper}
          >
            <BackIcon />
          </div>
        </div>

        <div className={styles.body}>
          <InstructorCard
            id={id}
            name={name}
            active={active}
            avatarUrl={avatarUrl}
            liveTopicCount={liveTopicCount}
            courseCount={courseCount}
            subjectString={subjectString}
            onCtaClick={onInstructorCardCtaClick}
            askableTutorId={askableTutorId}
            showTopicCount={showTopicList}
            showCourseCount={showCourseList}
            showScrollToContentButton={showContentSection}
            videoId={videoId}
            videoSource={videoSource}
          />
          <section className={styles.content}>
            {videoSource === 'youtube' && (
              <YouTube
                ref={playerRef}
                opts={youtubeOptions}
                containerClassName={styles.player}
                videoId={videoId}
              />
            )}
            {showAboutSection && <InstructorAbout aboutList={about} />}
            <div className={styles.sentinel} ref={contentListRef} />
            {showContentSection && (
              <div className={styles.contentMobileList}>
                <Tabs onChange={onContentMobileChange}>
                  {contentMobileTabPane}
                </Tabs>
                {getContentMobileCtaButton(contentMobileTab)}
              </div>
            )}

            <div className={styles.contentDesktopList}>
              {showTopicList && instructorTopicList}
              {showCourseList && instructorCourseList}
            </div>
          </section>
        </div>
      </div>
      <SnapaskFooter />
    </>
  );
}
InstructorProfile.propTypes = {
  id: number.isRequired,
};
export default InstructorProfile;
